<script setup lang="ts">
/**
 * 付款页：屏幕出码 → 顾客扫码 → 后端确认到账 → 出取餐号。
 *
 * 这里最要紧的两件事：
 *   1. **只有后端说 paid=true 才跳转。** 轮询失败、超时、看不懂的状态一律继续等，
 *      绝不因为「等久了」或者「请求成功了」就认为顾客付过钱。
 *   2. 二维码过期要自己换一张，并且告诉顾客「正在换」，而不是让他扫一张废码。
 *
 * 判定逻辑放在 `payments/pay-polling.ts` 里（纯函数、有单测），这里只负责执行。
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import QrPanel from '../components/QrPanel.vue';
import ModeBanner from '../components/ModeBanner.vue';
import { kiosk } from '../stores/kiosk';
import { decidePayPhase, safePollIntervalMs, type PayPhase } from '../payments/pay-polling';

const router = useRouter();

const phase = ref<PayPhase>('waiting');
const message = ref('正在生成付款二维码…');
const reissuing = ref(false);
let timer: ReturnType<typeof setTimeout> | null = null;
let stopped = false;

const intent = computed(() => kiosk.state.payIntent);
const order = computed(() => kiosk.state.order);
const expiresAtMs = computed(() => (intent.value ? new Date(intent.value.expiresAt).getTime() : 0));
/** 是否显示「模拟确认收款」按钮：部署方允许 + 后端确实是模拟模式 */
const canTestConfirm = computed(
  () => kiosk.state.config.allowTestPay && intent.value?.testConfirmPath !== null && intent.value?.isSimulated === true,
);

/** 出码（首次进入，或过期后换一张） */
async function issue(): Promise<void> {
  reissuing.value = true;
  message.value = '正在生成付款二维码…';
  const ok = await kiosk.startPay();
  reissuing.value = false;
  if (ok) {
    phase.value = 'waiting';
    message.value = intent.value?.alreadyPaid ? '这一单已经付过了' : '请用微信扫码付款';
    if (intent.value?.alreadyPaid) {
      void finish();
      return;
    }
    schedule();
  } else {
    // 出码失败（后端连不上等）：提示顾客叫服务员，同时留在本页可重试
    message.value = kiosk.state.notice?.text ?? '二维码没出来，请叫服务员';
  }
}

function schedule(): void {
  if (stopped) return;
  const interval = safePollIntervalMs(intent.value?.pollIntervalMs);
  if (timer) clearTimeout(timer);
  timer = setTimeout(poll, interval);
}

async function poll(): Promise<void> {
  if (stopped) return;
  const status = await kiosk.fetchPayStatus();
  const decision = decidePayPhase(status, Date.now(), expiresAtMs.value);
  phase.value = decision.phase;
  message.value = decision.message;

  if (decision.phase === 'paid') {
    void finish();
    return;
  }
  if (decision.phase === 'cancelled') return; // 不再轮询，等顾客/店员处理
  if (decision.phase === 'expired') {
    await issue();
    return;
  }
  schedule();
}

/** 付好了：停下轮询，跳去取餐号页 */
async function finish(): Promise<void> {
  stopped = true;
  if (timer) clearTimeout(timer);
  if (order.value) {
    order.value.payStatus = 'PAID';
  }
  void router.push('/done');
}

/** 模拟通道：仅在联调时出现的按钮 */
async function testConfirm(): Promise<void> {
  const ok = await kiosk.confirmTestPay();
  if (ok) await finish();
}

/** 放弃付款，回到首页（订单会留给店员处理） */
function abandon(): void {
  stopped = true;
  if (timer) clearTimeout(timer);
  kiosk.resetToIdle();
  void router.push('/');
}

onMounted(() => {
  if (!order.value) {
    void router.replace('/');
    return;
  }
  void issue();
});

onBeforeUnmount(() => {
  stopped = true;
  if (timer) clearTimeout(timer);
});
</script>

<template>
  <div class="screen pay">
    <div class="topbar">
      <div>
        <div class="topbar__title">扫码付款</div>
        <div class="topbar__sub">
          订单 {{ order?.orderNo }}
          <span v-if="order?.pickupNo"> · 取餐号 {{ order.pickupNo }}</span>
        </div>
      </div>
      <button class="btn btn--ghost btn--nowrap" data-testid="abandon" @click="abandon">
        <span class="hide-narrow">不点了，</span>回首页
      </button>
    </div>

    <ModeBanner :offline="kiosk.state.offline" />

    <div class="pay__body screen__body">
      <div v-if="reissuing || !intent" class="loading">正在生成付款二维码…</div>

      <QrPanel
        v-else
        :payload="intent.qrPayload"
        :amount-cents="intent.amountCents"
        :expires-at="intent.expiresAt"
        :is-simulated="intent.isSimulated"
        @expire="issue"
      />

      <div class="pay__status" :class="`pay__status--${phase}`">{{ message }}</div>

      <div v-if="intent?.isSimulated" class="pay__test">
        <div class="muted">
          模拟模式：顾客扫码不会真的付钱。联调时可以点下面的按钮走完流程，
          这笔收款会标成「测试」，<b>不计入营业额</b>。
        </div>
        <button
          v-if="canTestConfirm"
          class="btn btn--subtle"
          data-testid="test-confirm"
          @click="testConfirm"
        >
          测试：确认已收款
        </button>
      </div>

      <div class="pay__help muted">
        付完款屏幕会自动跳到取餐号；如果一直没反应，请把这张屏幕给店员看
      </div>
    </div>
  </div>
</template>

<style scoped>
.btn--nowrap {
  white-space: nowrap;
}

.pay__body {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  overflow: auto;
}

.pay__status {
  font-size: 24px;
  font-weight: 700;
  padding: 10px 22px;
  border-radius: 999px;
  background: var(--panel-2);
}

.pay__status--paid {
  background: rgba(47, 169, 107, 0.2);
  color: #8ff0bd;
}

.pay__status--expired,
.pay__status--cancelled {
  background: rgba(232, 163, 61, 0.2);
  color: #ffd08a;
}

.pay__test {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  max-width: 620px;
  text-align: center;
  font-size: 15px;
}

.pay__help {
  font-size: 15px;
}
</style>
